// One network list (D81): its domains (search, add, upload, remove), its name and description,
// the task groups that use it, and deleting it (refused while a group uses it).
import { Trash } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "rizzui";

import { useT } from "../i18n";
import { detailCrumbs } from "../layout/nav";
import { toast } from "../layout/toast";
import { deleteList, networkListQuery } from "../network/api";
import { DetailsCard, UsedByCard } from "../network/DetailsCard";
import { DomainsCard } from "../network/DomainsCard";
import { useListChange } from "../network/useListChange";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { PageHeader } from "../ui/PageHeader";
import { ErrorState, Loading } from "../ui/states";

export default function NetworkListPage() {
  const { network, shell } = useT();
  const t = network.list;
  const { listId = "" } = useParams();
  const list = useQuery(networkListQuery(listId));
  const change = useListChange();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const crumbs = (name: string) => detailCrumbs("network", "/network", { name });

  if (list.isPending) return <Loading />;
  if (list.isError) {
    return (
      <>
        <PageHeader title={shell.pages.networkList.title} breadcrumb={crumbs(shell.pages.notFound.title)} />
        <ErrorState title={t.loadFailed} message={list.error.message} />
      </>
    );
  }
  const l = list.data;
  return (
    <>
      <PageHeader title={l.name} breadcrumb={crumbs(l.name)} description={l.description || shell.pages.networkList.subtitle}>
        <Button variant="outline" color="danger" className="gap-1.5" onClick={() => setDeleting(true)}>
          <Trash weight="bold" className="h-4 w-4" /> {t.delete}
        </Button>
      </PageHeader>
      <div className="grid gap-5 xl:grid-cols-12">
        <DomainsCard className="min-w-0 xl:col-span-8" list={l} change={change} />
        <div className="min-w-0 space-y-5 xl:col-span-4">
          <DetailsCard key={l.updated_at} list={l} change={change} />
          <UsedByCard list={l} />
        </div>
      </div>
      <ConfirmDialog
        open={deleting}
        title={t.deleteTitle(l.name)}
        label={t.deleteTitle(l.name)}
        confirmLabel={t.delete}
        onClose={() => setDeleting(false)}
        onConfirm={async () => {
          await deleteList(l.id);
          await queryClient.invalidateQueries({ queryKey: ["network-lists"] });
          toast(t.deleted(l.name));
          void navigate("/network");
        }}
      >
        {t.deleteBody}
      </ConfirmDialog>
    </>
  );
}
